'use client';
/**
 * Creates a namespaced slice that writes under `state[name]` and exposes
 * actions that are bound to that namespace. This is a thin helper; the
 * store factory composes multiple such slices.
 */
export function createSlice(contract) {
    const { name, initialState, actions, selectors, persist, reset } = contract;
    function build(store) {
        // Scoped helpers to the namespace
        const getSlice = () => (store.getState()[name] ?? initialState);
        const setSlice = (updater) => {
            // With immer middleware enabled on the store, setState can accept a
            // recipe that mutates the draft. We mutate only the namespaced slice
            // to preserve references for other namespaces.
            store.setState((draft) => {
                const current = draft[name] ?? initialState;
                if (typeof updater === 'function') {
                    const result = updater(current);
                    // If the updater returned a partial, merge into current
                    if (result && result !== current) {
                        Object.assign(current, result);
                    }
                }
                else {
                    Object.assign(current, updater);
                }
                draft[name] = current;
            });
        };
        const ctx = {
            setSlice,
            getSlice,
            set: store.setState,
            get: store.getState,
            name,
        };
        const boundActions = actions(ctx);
        // Build slice state with actions and a bound reset
        const sliceObject = {
            ...initialState,
            ...boundActions,
            reset: () => setSlice({ ...initialState }),
        };
        return sliceObject;
    }
    // Expose persistable keys so the factory can partialize correctly
    const persistKeys = persist?.keys ?? undefined;
    return { name, build, persistKeys };
}
